import React, { useCallback, useEffect, useState } from 'react';
import { AfterPayBanner, AlertsList, EmptyProductsFallback } from '../../components';
import {
    BREADCRUMB,
    GRADPRODUCTTILES,
    HELPBUTTON,
    JWLPAYPALBANNER,
    PACKAGESGRID,
    PAGETITLE,
    PROMOMSG
} from '../../constants/rootElements';
import { PromoCodesProvider, TermsAndConditionsProvider } from '../../context';
import { useFeeAlertInjectionScript, useGoogleAnalyticsHandler } from "../../hooks";
import { appendTermsAndConditionsLink } from '../../util/appendTermsAndConditionsLink';
import { filterObjectsByNonEmptyKey } from '../../util/filterObjectsByNonEmptyKey';
import { gaAllSelectionsPageLoad } from '../../util/googleAnalytics';
import { Breadcrumb } from '../breadcrumb';
import { HelpButton } from '../help';
import { TitleBar } from "../productBrowseTitle";
import GradPackageTabs from './gradPackageTabs';
import { HsGradProductBrowse } from './gradProductBrowse';

export const HSGradPage = ({ appData }) => {
    const [ pbData, setPbData ] = useState({})
    const [breadcrumbData, setBreadcrupmData] = useState([])
    const [promoMessages, setPromoMessages] = useState([]);
    const [feeAlertRequestData, setFeeAlertRequestData] = useState(null)
    const [googleAnalyticsRequestData, setGoogleAnalyticsRequestData] = useState(null);
    const [schoolStoreEvents, setSchoolStoreEvents] = useState([]);
    const [currencyCode] = useState(null);

    useFeeAlertInjectionScript(feeAlertRequestData)
    const { selectItemTrackingGASY25, viewModalTrackingGASY25, addToCartTrackingGASY25 } = useGoogleAnalyticsHandler(googleAnalyticsRequestData);
    
    const gradTitleBar = appData?.pagecontentData?.find(data => data.contentType === "ProductBrowseTitle")
    const gradBreadcrumb = Boolean(appData?.pagecontentData?.find(data => data.contentType === "Navigation"))
    const ProductBrowsePromoMsg = Boolean(appData?.pagecontentData?.find(data => data.contentType === "ProductBrowsePromoMsg"))
    const ProductBrowseProducts = appData?.pagecontentData?.find(data => data.contentType === "ProductBrowseProducts")
    const GradPaymentOptions = appData?.pagecontentData?.find(data => data.contentType === "PaymentOptions")
    const PaypalBannerData = appData?.pagecontentData?.find(data => data.contentType === "PaypalId")
    const ProductBrowseFlavoredProducts = appData?.pagecontentData?.find(data => data.contentType === "ProductBrowseFlavoredProducts")
    const ProductBrowsePackages = appData?.pagecontentData?.find(data => data.contentType === "Packages")
    const HsPaymentPromo = appData?.pagecontentData?.find(data => data.contentType === "PaymentPromo")
    const initialData = { promoCodes: appData.PromoCodesData, excludeList: appData.excludeListData }

    const nonEmptyPromoMessages = filterObjectsByNonEmptyKey(promoMessages, 'message')

	const getBreadcrumb = useCallback(() => {
		BREADCRUMB.render(<Breadcrumb pbData={breadcrumbData} />);
    },[breadcrumbData])

    const getPromoMsg = useCallback(() => {
        if (!!nonEmptyPromoMessages?.length) {
            const messagesWithLink = appendTermsAndConditionsLink(nonEmptyPromoMessages);
            PROMOMSG.render(
                <AlertsList messages={messagesWithLink} />
            );
        }
    }, [nonEmptyPromoMessages])

    const getCatalogDataFromUrl = useCallback(() => {
        let catalogData = null;
        const pathNameParts = window.location.pathname.split('/')
        if (pathNameParts?.length >= 9) {
            catalogData = {
                customerNumber: pathNameParts[4],
                publishedCatalogId: pathNameParts[7],
                sourceCatalogType: pathNameParts[8]
            }
        }
        return catalogData;
    }, [])

    const paypalBanner = useCallback(() => {
        JWLPAYPALBANNER.render(<AfterPayBanner currencyCode={currencyCode} />);
    },[PaypalBannerData, currencyCode])

    const getPackagesGrid = useCallback(() => {
        const productsData = { ...pbData }
        if (productsData && Object.keys(productsData)?.length > 0 && productsData.groupOsr === ProductBrowsePackages?.ctaCheck?.groupOsr) {
            PACKAGESGRID.render(<GradPackageTabs 
                packageList={productsData?.packageItems?.filter(obj => obj.packageProductAvailability?.length > 0)} 
                productList={[...productsData?.products?.filter(obj => obj.productType === ProductBrowsePackages?.ctaCheck?.productType)]} 
                pageContentData={ProductBrowsePackages} hsPaymentPromo={HsPaymentPromo} altImage = {ProductBrowseProducts?.imageUrl}
                googleAnalytics={{ 
                    selectItemTrackingGASY25, 
                    viewModalTrackingGASY25, 
                    addToCartTrackingGASY25,
                    catalogOSR: googleAnalyticsRequestData?.catalogOsr
                }}
            />);
        }
        
    }, [pbData, ProductBrowsePackages, HsPaymentPromo, selectItemTrackingGASY25])

    const productTiles = useCallback(() => {
        const productsData = { ...pbData }
        if (productsData && Object.keys(productsData)?.length > 0 && productsData.groupOsr === "REGALIA") {
            productsData.products = productsData.products.filter((obj => obj.productType !== "PACKAGE"))
        }
        GRADPRODUCTTILES.render(
            !!pbData?.products?.length
                ? <PromoCodesProvider initialData={initialData}>
                    <TermsAndConditionsProvider>
                        <HsGradProductBrowse
                            pbData={productsData}
                            pageContent={ProductBrowseProducts}
                            filterData={appData.filterData}
                            gradPaymentOptions={GradPaymentOptions}
                            flavoredProductsPageContent={ProductBrowseFlavoredProducts}
                            schoolStoreEvents={schoolStoreEvents}
                            googleAnalytics={{
                                selectItemTrackingGASY25,
                                viewModalTrackingGASY25,
                                addToCartTrackingGASY25,
                                catalogOSR: googleAnalyticsRequestData?.catalogOsr
                            }}
                        />
                    </TermsAndConditionsProvider>
                </PromoCodesProvider>
                : <EmptyProductsFallback
                    productBrowseData={pbData}
                />
        )
        // eslint-disable-next-line
    }, [
        pbData,
        ProductBrowseProducts,
        appData.filterData,
        GradPaymentOptions,
        ProductBrowseFlavoredProducts,
        selectItemTrackingGASY25
    ])

    const getHelpButton = useCallback(() => {
            HELPBUTTON.render(<HelpButton />);
    },[])

    const getTitleBar = useCallback(() => {
        PAGETITLE.render(<TitleBar pageContent={gradTitleBar} affiliationData={appData?.affiliationData}/>);
    },[])
	
	useEffect(()=> {
		Object.keys(appData.productBrowseData).map((key) => {
            if(key !== "metaData"){
                const catalog = appData.productBrowseData[key];
                catalog && Object.keys(catalog)?.length > 0 && 
                    setPbData(catalog)
                    setBreadcrupmData(catalog)
                    setPromoMessages(catalog?.promoMessagesV2)
                    const customerNumber = appData.productBrowseData.metaData.customerNumber;
                    setFeeAlertRequestData({
                        containerSelector: '#promoMsg',
                        customerId: customerNumber,
                        catalogOsr: catalog.catalogOsr,
                    });
                    const catalogData = getCatalogDataFromUrl();
                    catalog?.relatedSchoolEvents?.length
                        && setSchoolStoreEvents([...catalog.relatedSchoolEvents])    
                    setGoogleAnalyticsRequestData({
                        customerNumber,
                        publishedCatalogId: catalog?.publishedCatalogId || catalogData?.publishedCatalogId,
                        sourceCatalogType: catalog?.sourceCatalogType || catalogData?.sourceCatalogType,
                        catalogOsr: catalog.catalogOsr
                    });
            }
            return true
        })
    },[appData.productBrowseData])
    
    useEffect(()=>{
        if(appData.productBrowseData && Object.keys(appData.productBrowseData).length>0){
            let key = Object.keys(appData.productBrowseData)[0];
            appData.productBrowseData[key].displayName==="Cap & Gown" && gaAllSelectionsPageLoad();
        }
    },[])

	return (
		<>
        {
            gradTitleBar && Object.keys(gradTitleBar)?.length > 0 && getTitleBar()
        }
	    {
            gradBreadcrumb && getBreadcrumb() 
        }
        {
            ProductBrowsePromoMsg && promoMessages?.length > 0 && getPromoMsg()
        }
        {
            ProductBrowsePackages && Object.keys(ProductBrowsePackages)?.length > 0 && getPackagesGrid()
        }
        {
            ProductBrowseProducts && Object.keys(ProductBrowseProducts)?.length > 0 && productTiles()
        }
        {
            getHelpButton()
        }
        {
            PaypalBannerData && Object.keys(PaypalBannerData)?.length > 0 && paypalBanner()
        }
			
		</>
	)
}
